import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import { auth } from "../firebaseConfig";
import { useAppSelector } from "../store";
import { runActivationPipelines, RunActivationPipelinesResult } from "../utils/http/admin";

function Admin() {
  const user = useAppSelector((state) => state.user);

  // --- Token Info state ---
  const [idToken, setIdToken] = useState("");
  const [mimicMsg, setMimicMsg] = useState("");

  useEffect(() => {
    const fetchToken = async () => {
      const retrievedIdToken = await auth?.currentUser?.getIdToken();
      setIdToken(retrievedIdToken || "");
    };
    fetchToken();
  }, []);

  const saveToClipboard = () => {
    navigator.clipboard.writeText(idToken);
  };

  const mimic = async () => {
    const retrievedIdTokenResult = await auth?.currentUser?.getIdTokenResult();
    const retrievedIdToken = retrievedIdTokenResult?.token;
    if (retrievedIdToken !== idToken) {
      setIdToken(retrievedIdToken || "");
      setMimicMsg(
        `Fetched new token. Expiration time: ${retrievedIdTokenResult?.expirationTime}`
      );
      return;
    }
    setMimicMsg(
      `Using old token. Expiration time: ${retrievedIdTokenResult?.expirationTime}`
    );
  };

  // --- Activation Pipelines state ---
  const [notifySlackBlocked, setNotifySlackBlocked] = useState(false);
  const [notifySlackSuccess, setNotifySlackSuccess] = useState(false);
  const [pipelineResult, setPipelineResult] = useState<RunActivationPipelinesResult | null>(null);

  const {
    mutate: mutateRunPipelines,
    isPending: isPipelinesRunning,
    error: pipelinesError,
  } = useMutation({
    mutationFn: runActivationPipelines,
    onSuccess: (data) => {
      setPipelineResult(data);
    },
    onError: () => {
      setPipelineResult(null);
    },
  });

  const handleRunPipelines = () => {
    mutateRunPipelines({ notifySlackBlocked, notifySlackSuccess });
  };

  const hasTokenCopy = user?.roles?.includes("token-copy");
  const hasAdminEndpoints = user?.roles?.includes("admin-endpoints");

  return (
    <div>
      <div className="mb-4">
        <h3 className="mb-1">Admin</h3>
      </div>
      <hr className="mb-4" />

      <Row className="g-4">
        {hasTokenCopy && (
          <Col md={6}>
            <Card className="h-100">
              <Card.Header className="bg-light">
                <h5 className="mb-0">
                  <i className="bi bi-key me-2"></i>
                  Token Info
                </h5>
              </Card.Header>
              <Card.Body>
                <div className="mb-3">
                  <strong>ID Token:</strong>{" "}
                  <Button variant="outline-primary" size="sm" onClick={saveToClipboard}>
                    <i className="bi bi-clipboard me-1"></i>
                    ...{idToken?.slice(-50)}
                  </Button>
                </div>
                <div>
                  <Button variant="secondary" size="sm" onClick={mimic}>
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Mimic getting token for sending request
                  </Button>{" "}
                  {mimicMsg && <span className="text-muted">{mimicMsg}</span>}
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}

        {hasAdminEndpoints && (
          <Col md={6}>
            <Card className="h-100">
              <Card.Header className="bg-light">
                <h5 className="mb-0">
                  <i className="bi bi-gear me-2"></i>
                  Run Activation Pipelines
                </h5>
              </Card.Header>
              <Card.Body>
                <Row className="mb-3">
                  <Col xs={12} className="mb-2">
                    <Form.Check
                      type="switch"
                      id="notify-slack-blocked"
                      label="Notify Slack (blocked pipelines)"
                      checked={notifySlackBlocked}
                      onChange={(e) => setNotifySlackBlocked(e.target.checked)}
                    />
                  </Col>
                  <Col xs={12}>
                    <Form.Check
                      type="switch"
                      id="notify-slack-success"
                      label="Notify Slack (completed pipelines)"
                      checked={notifySlackSuccess}
                      onChange={(e) => setNotifySlackSuccess(e.target.checked)}
                    />
                  </Col>
                </Row>
                <div className="mb-3">
                  <Button
                    variant="primary"
                    onClick={handleRunPipelines}
                    disabled={isPipelinesRunning}
                  >
                    {isPipelinesRunning ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Running...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-play-fill me-2"></i>
                        Run Activation Pipelines
                      </>
                    )}
                  </Button>
                </div>

                {pipelinesError && (
                  <Alert variant="danger">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {(pipelinesError as any)?.info?.detail || pipelinesError.message || "An error occurred."}
                  </Alert>
                )}

                {pipelineResult && (
                  <Table bordered size="sm" className="mt-3">
                    <tbody>
                      <tr>
                        <td><strong>Total Onboarding</strong></td>
                        <td>{pipelineResult.total_onboarding}</td>
                      </tr>
                      <tr>
                        <td><strong>Total with Pipeline</strong></td>
                        <td>{pipelineResult.total_with_pipeline}</td>
                      </tr>
                      <tr>
                        <td><strong>Completed</strong></td>
                        <td><Badge bg="success">{pipelineResult.total_completed}</Badge></td>
                      </tr>
                      <tr>
                        <td><strong>Blocked</strong></td>
                        <td><Badge bg="warning">{pipelineResult.total_blocked}</Badge></td>
                      </tr>
                      <tr>
                        <td><strong>Failed</strong></td>
                        <td><Badge bg="danger">{pipelineResult.total_failed}</Badge></td>
                      </tr>
                      <tr>
                        <td><strong>Skipped</strong></td>
                        <td><Badge bg="secondary">{pipelineResult.total_skipped}</Badge></td>
                      </tr>
                      <tr>
                        <td><strong>Error</strong></td>
                        <td><Badge bg="danger">{pipelineResult.total_error}</Badge></td>
                      </tr>
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Col>
        )}

        {(hasTokenCopy || hasAdminEndpoints) && (
          <Col md={6}>
            <Card className="h-100">
              <Card.Header className="bg-light">
                <h5 className="mb-0">
                  <i className="bi bi-grid-3x3-gap me-2"></i>
                  Tools
                </h5>
              </Card.Header>
              <Card.Body>
                <Link to="/admin/mail-template-matrix" className="d-inline-flex align-items-center">
                  <i className="bi bi-table me-2"></i>
                  Mail-Template Coverage Matrix
                </Link>
                <div className="text-muted mt-1" style={{ fontSize: "0.85rem" }}>
                  Cross-company overview of active / live templates and conditional usage. Slow to load.
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
}

export default Admin;
